import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http } from "../lib/api";
import useCanonical, { SITE } from "../lib/useCanonical";
import usePageMeta from "../lib/usePageMeta";
import { CATEGORIES, categoryPath } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";

const JOURNAL_TITLE = "The Journal: Every Tea Article — The Tea List";
const JOURNAL_DESCRIPTION =
  "Browse every article from The Tea List — brewing guides, tea types, origins, regions, wellness and curious stories, written in Melbourne for Australian and New Zealand tea drinkers.";

export default function BlogList({ forcedCategory }) {
  // Canonical: forced-category pages canonicalise to their /category/{slug}
  // path. The plain /blog?cat=... variants point back to /blog to avoid
  // duplicate-content flags on filter URLs.
  const catMeta = forcedCategory ? CATEGORIES.find((c) => c.name === forcedCategory) : null;
  useCanonical(forcedCategory ? categoryPath(forcedCategory) : "/blog");
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  usePageMeta({
    title: q ? `Search: “${q}” — The Tea List` : catMeta?.title || JOURNAL_TITLE,
    description: catMeta?.description || JOURNAL_DESCRIPTION,
    noindex: Boolean(q),
    jsonLd: q
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          url: `${SITE}${forcedCategory ? categoryPath(forcedCategory) : "/blog"}`,
          name: catMeta?.title || JOURNAL_TITLE,
          description: catMeta?.description || JOURNAL_DESCRIPTION,
          isPartOf: { "@id": "https://thetealist.com/#website" },
          inLanguage: "en-AU",
        },
  });
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(forcedCategory || searchParams.get("category") || "All");
  const [search, setSearch] = useState(q);

  useEffect(() => {
    http.get("/posts/categories").then(({ data }) => setCats(data));
    // `http` is a stable module import; `setCats` is a stable React ref.
  }, []);  

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active && active !== "All") params.category = active;
    if (q) params.q = q;
    http.get("/posts", { params }).then(({ data }) => {
      setPosts(data);
      setLoading(false);
    });
    // setters + http are stable; only re-run on filter change.
  }, [active, q]);  

  useEffect(() => {
    if (forcedCategory) setActive(forcedCategory);
  }, [forcedCategory]);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) params.set("q", search);
    else params.delete("q");
    setSearchParams(params);
  };

  let pageEyebrow = "The Journal";
  if (forcedCategory) pageEyebrow = "Category";
  else if (q) pageEyebrow = "Search";

  let pageHeading;
  if (forcedCategory) {
    pageHeading = forcedCategory;
  } else if (q) {
    pageHeading = (
      <>
        Results for <span className="italic text-[#1e4a35]">&ldquo;{q}&rdquo;</span>
      </>
    );
  } else {
    pageHeading = (
      <>
        All writing,
        <br />
        <span className="italic">one cup at a time.</span>
      </>
    );
  }

  let resultsBlock;
  if (loading) {
    resultsBlock = (
      <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
        steeping…
      </p>
    );
  } else if (posts.length === 0) {
    resultsBlock = (
      <div className="py-20 text-center">
        <p className="font-serif text-3xl font-light text-[#404e48]">No posts — yet.</p>
        <Link to="/blog" className="inline-block mt-6 btn-secondary">
          Back to all
        </Link>
      </div>
    );
  } else {
    resultsBlock = (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
        {posts.map((p) => (
          <ArticleCard key={p.slug} post={p} />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14 relative z-10" data-testid="blog-list-page">
      <div className="mb-10">
        <p className="overline mb-4">{pageEyebrow}</p>
        <h1 className="headline-xl text-5xl md:text-7xl">{pageHeading}</h1>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8 border-b pb-5" style={{ borderColor: "var(--border)" }}>
        <div className="flex flex-wrap gap-2" data-testid="category-filter-bar">
          {!forcedCategory &&
            ["All", ...cats].map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase transition-colors ${
                  active === c
                    ? "bg-[#1e4a35] text-white"
                    : "bg-[#f1eadb] text-[#404e48] hover:bg-[#e2d9c6]"
                }`}
                data-testid={`filter-category-${c}`}
              >
                {c}
              </button>
            ))}
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2 border-b border-[#151e1a]/40 focus-within:border-[#1e4a35]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles…"
            className="bg-transparent outline-none text-sm py-2 min-w-[200px]"
            data-testid="blog-search-input"
          />
          <button type="submit" className="font-mono text-[10px] uppercase tracking-widest text-[#1e4a35]" data-testid="blog-search-submit">
            Go
          </button>
        </form>
      </div>

      {resultsBlock}
    </div>
  );
}
